import type { IObservableArray } from 'mobx';
import { action, computed, makeObservable } from 'mobx';

import { concatPath } from '@feathr/hooks';
import type {
  Attributes,
  DeepObservable,
  IBaseAttributes,
  IListOptions,
  IListParams,
  IRachisMessage,
  ListResponse,
  TConstraints,
} from '@feathr/rachis';
import { Collection, DisplayModel, isWretchError, wretch } from '@feathr/rachis';

import type { IBannersnackAttributes, IBannersnackClass } from './bannersnack';
import type { IMergeField } from './campaigns';
import type { CustomField } from './custom_fields';
import { FieldCollection } from './custom_fields';
import type { IAggregatedStats } from './stats_aggregated';
import type { IUser } from './users';

export enum TemplateClass {
  Email = 'Template.HtmlTemplate.EmailTemplate',
  ReferralEmail = 'Template.HtmlTemplate.EmailTemplate.ReferralEmailTemplate',
  Page = 'Template.HtmlTemplate.PageTemplate',
  ReferralPage = 'Template.HtmlTemplate.PageTemplate.ReferralPageTemplate',
  Banner = 'Template.HtmlTemplate.PageTemplate.BannerTemplate',
  ReferralBanner = 'Template.HtmlTemplate.PageTemplate.ReferralBannerTemplate',
  LandingPage = 'Template.HtmlTemplate.PageTemplate.LandingPageTemplate',
  Link = 'Template.LinkTemplate',
  SESEmail = 'Template.HtmlTemplate.SESEmailTemplate',
}

export enum RecipientType {
  Person = 'Person',
  Partner = 'Partner',
}

interface ITemplateMergemap extends Record<string, unknown> {
  mergemap: Record<string, string>;
}

interface ITemplateEditorURLResponse extends Record<string, unknown> {
  url: string;
}

interface ITemplatePreviewPostResponse extends Record<string, unknown> {
  preview_url: string;
}

interface IMentionableUser extends Record<string, unknown>, Pick<IUser, 'id' | 'name' | 'email'> {
  color: IUser['settings']['bee_user_color'];
}

export const defaultProjectMergeFields = [
  {
    type: 'tag',
    name: 'Project name',
    value: '@EVENT_NAME@',
  },
  {
    type: 'tag',
    name: 'Project start date',
    value: '@EVENT_DATE_START@',
  },
  {
    type: 'tag',
    name: 'Project end date',
    value: '@EVENT_DATE_END@',
  },
  {
    type: 'tag',
    name: 'Project location',
    value: '@EVENT_LOCATION@',
  },
  {
    type: 'link',
    name: 'Project link',
    value: '@EVENT_URL@',
  },
  {
    type: 'link',
    name: 'Project registration link',
    value: '@EVENT_REG_URL@',
  },
  {
    type: 'content',
    name: 'Project logo image',
    value: '@EVENT_LOGO_IMG@',
  },
  {
    type: 'tag',
    name: 'Project logo URL',
    value: '@EVENT_LOGO_URL@',
  },
];

export const defaultPersonMergeFields = [
  {
    type: 'tag',
    name: 'Name',
    value: '@PERSON_NAME@',
  },
  {
    type: 'tag',
    name: 'Primary email',
    value: '@PERSON_EMAIL@',
  },
];

export const defaultPartnerMergeFields = [
  {
    type: 'tag',
    name: 'Partner name',
    value: '@PARTNER_NAME@',
  },
  {
    type: 'content',
    name: 'Partner logo image',
    value: '@PARTNER_LOGO_IMG@',
  },
  {
    type: 'tag',
    name: 'Partner logo url',
    value: '@PARTNER_LOGO_URL@',
  },
  {
    type: 'tag',
    name: 'Partner email',
    value: '@PARTNER_EMAIL@',
  },
  {
    type: 'tag',
    name: 'Partner external id',
    value: '@PARTNER_EXTERNAL_ID@',
  },
  {
    type: 'link',
    name: 'Partner website',
    value: '@PARTNER_WEBSITE@',
  },
  {
    type: 'link',
    name: 'Partner dashboard link',
    value: '@PARTNER_DASHBOARD@',
  },
  {
    type: 'tag',
    name: 'Partner description',
    value: '@PARTNER_DESCRIPTION@',
  },
  {
    type: 'tag',
    name: 'Download leads (CSV)',
    value: '@PARTNER_LEADS_URL@',
  },
];

export const participantPageUrlField = {
  type: 'link',
  name: 'Partner Page URL',
  value: '@CAMPAIGN.PARTICIPANT.PAGE_URL@',
};

export const referralPageUrlField = {
  type: 'tag',
  name: 'Referral Page URL',
  value: '@CAMPAIGN.PARTICIPANT.PAGE_URL@',
};

export const campaignDestinationUrlField = {
  type: 'link',
  name: 'Campaign Destination URL',
  value: '@CAMPAIGN.DESTINATION_URL@',
};

export const sesRecipientMergeFields = [
  {
    type: 'tag',
    name: 'Primary email',
    pinpointValue: '{{Attributes.Email}}',
    value: '{{email_address}}',
    noDefault: true,
  },
  {
    type: 'tag',
    name: 'Name',
    pinpointValue: '{{User.UserAttributes.name}}',
    value: '{{name}}',
  },
];

export const sesPersonMergeFields = [
  {
    type: 'tag',
    name: 'First name',
    pinpointValue: '{{User.UserAttributes.first_name}}',
    value: '{{first_name}}',
  },
  {
    type: 'tag',
    name: 'Last name',
    pinpointValue: '{{User.UserAttributes.last_name}}',
    value: '{{last_name}}',
  },
  {
    type: 'tag',
    name: 'Company',
    pinpointValue: '{{User.UserAttributes.companies}}',
    value: '{{companies}}',
  },
  {
    type: 'tag',
    name: 'Occupation',
    pinpointValue: '{{User.UserAttributes.occupation}}',
    value: '{{occupation}}',
  },
];

export const sesPartnerMergeFields = [
  {
    type: 'content',
    name: 'Partner logo image',
    pinpointValue: '{{User.UserAttributes.logo_img}}',
    value: '{{logo_img}}',
  },
  {
    type: 'tag',
    name: 'Partner logo url',
    pinpointValue: '{{User.UserAttributes.logo_url}}',
    value: '{{logo_url}}',
  },
  {
    type: 'tag',
    name: 'Partner external id',
    pinpointValue: '{{User.UserAttributes.external_id}}',
    value: '{{external_id}}',
  },
  {
    type: 'link',
    name: 'Partner website',
    pinpointValue: '{{User.UserAttributes.website}}',
    value: '{{website}}',
  },
  {
    type: 'link',
    name: 'Partner dashboard link',
    pinpointValue: '{{User.UserAttributes.dashboard_url}}',
    value: '{{dashboard_url}}',
  },
  {
    type: 'tag',
    name: 'Partner description',
    pinpointValue: '{{User.UserAttributes.description}}',
    value: '{{description}}',
  },
  {
    type: 'tag',
    name: 'Number of leads',
    pinpointValue: '{{User.UserAttributes.leads}}',
    value: '{{leads}}',
  },
  {
    type: 'tag',
    name: 'Download leads (CSV)',
    pinpointValue: '{{User.UserAttributes.leads_url}}',
    value: '{{leads_url}}',
  },
];

export enum DynamicContentClass {
  CountdownTimer = 'DynamicContent.CountdownTimer',
  Form = 'DynamicContent.DynamicForm',
}

export interface IFormField {
  tag: string;
  name: string;
  placeholder: string;
  label: string;
  type_: string;
  required: boolean;
}

export interface IDynamicContent {
  macro_prefix: string;
  name: string;
  content_index: number;
  _cls: DynamicContentClass;

  font?: string;
  background_color?: string;
  title_color?: string;
  shortlink_color?: string;
  stat_color?: string;
  show_stats?: boolean;
  width?: number;

  color?: string;
  action?: 'update' | 'custom';
  onsubmit?: string;
  submit?: string;
  title?: string;
  subtitle?: string;
  complete?: string;
  fields?: IFormField[];

  frames?: number;
  time?: string;
}

export interface IMetaTags {
  description: string;
  author: string;
  og_title: string;
  og_type: string;
  og_image: string;
  og_image_height: number;
  og_image_width: number;
  twitter_site: string;
  twitter_creator: string;
}

export interface ITemplateDashboardConfig {
  title?: string;
}

export type TTemplateParentKind = 'account' | 'campaign' | 'event';

export interface ITemplate extends IBaseAttributes, IBannersnackAttributes {
  _cls: TemplateClass;
  account?: string;
  /** Gets set to true after calling <pk>/create-banner and bannersnack generated the banner */
  bannersnack_enabled?: boolean;
  /** Writeonly */
  campaign_id?: string;
  content: {
    _cls: string;
    /*
     * When unparsed, the json object contains a "page" attribute that
     * we dereference for template json.
     */
    json: string;
  };
  event?: string | null;
  /** Writeonly */
  event_id?: string | null;
  dashboard_config: ITemplateDashboardConfig;
  date_last_modified: string;
  date_published: string;
  default: boolean;
  dynamic_content: IDynamicContent[];
  extra_footer_html: string;
  extra_header_html: string;
  height?: number;
  mergefield_defaults: Record<string, string>;
  metatags: IMetaTags;
  name?: string;
  parent_kind: TTemplateParentKind;
  parent: string;
  /** The preview text displayed after the subject line in the email client */
  preview_text: string;
  read_only: boolean;
  short_code: string;
  source_id?: string;
  /**
   * Subject of an email template.
   * Defaults to an empty string. Should not be null or undefined.
   */
  subject: string;
  stats_cache?: Partial<IAggregatedStats>;
  thumbnail_url: string;
  used_mergefields: string[];
  width?: number;
}

export interface ILinkTemplate {
  readonly id?: string;
  _cls: TemplateClass.Link;
  to: string;
  thumbnail_url: string;
  title: string;
}

export interface ISendOptions {
  email: string;
  from_: string;
  from_name: string;
  test_subject?: string;
}

export interface IPreviewBody extends ISendOptions {
  partner_id?: string;
  person_id?: string;
  campaign_id?: string;
  invites_campaign_id?: string;
  event_id?: string | null;
  template: ITemplate;
  send?: true;
  creative_id?: string;
}

export function templateTypeLabel(type: TemplateClass): string {
  const typeMap: Record<TemplateClass, string> = {
    [TemplateClass.Email]: 'Email',
    [TemplateClass.ReferralEmail]: 'Invite email',
    [TemplateClass.Page]: 'Page',
    [TemplateClass.LandingPage]: 'Landing page',
    [TemplateClass.ReferralPage]: 'Invite page',
    [TemplateClass.Banner]: 'Banner',
    [TemplateClass.ReferralBanner]: 'Invite Banner',
    [TemplateClass.Link]: 'Link',
    [TemplateClass.SESEmail]: 'Email',
  };
  return typeMap[type];
}

interface ICloneProps {
  campaign_id?: string;
  event_id?: string | null;
  /** Required, even though the interface requires it to be optional. */
  _cls?: string;
}

export class Template extends DisplayModel<ITemplate> implements IBannersnackClass {
  public readonly className = 'Template';

  public get constraints(): TConstraints<ITemplate> {
    return {
      name: {
        presence: {
          allowEmpty: false,
        },
      },
      subject: {
        presence: {
          allowEmpty: false,
        },
      },
      short_code: {
        presence: {
          allowEmpty: false,
          message: '^URL Template cannot be blank.',
        },
        length: {
          minimum: 3,
          tooShort: '^URL Template must have at least 3 characters.',
        },
        format: (...args: any[]) => {
          // args = value, attributes, attributeName, options, constraints
          const _cls = args[3].model.get('_cls');
          if (_cls === TemplateClass.LandingPage) {
            return {
              pattern: '^[a-zA-Z0-9_-]+$',
              message: '^Template URL can only contain letters, numbers, dashes, and underscores.',
            };
          }
          // Campaigns other than Landing Pages can have special characters, e.g. merge tags
          return undefined;
        },
        async: {
          fn: async (value: string | undefined, model: Template): Promise<string[] | undefined> => {
            if (!value) {
              return;
            }
            // An ephemeral (unsaved) template has a client-minted id the backend doesn't own,
            // so POST templates/{id}/validate_short_code 404s once the endpoint is
            // account-scoped (Feathr/anhinga#7667). Defer validation until it's persisted —
            // returning undefined leaves the field valid-for-now (re-validated on save).
            if (model.isEphemeral && !model.isPending) {
              return;
            }
            if (!model.collection) {
              return [
                '^Model does not have a collection and therefore validity cannot be determined.',
              ];
            }
            const response = await wretch(
              `${model.collection.url()}${this.id}/validate_short_code`,
              {
                method: 'POST',
                headers: model.collection.getHeaders(),
                body: JSON.stringify({ short_code: value }),
              },
            );
            if (isWretchError(response)) {
              throw response.error;
            }
            const validShortCode = response.data ? response.data['is_valid_short_code'] : false;
            if (!validShortCode) {
              return ['^Short code must be unique.'];
            }
            return undefined;
          },
        },
      },
    };
  }

  constructor(initialAttributes: Partial<ITemplate> = {}) {
    super(initialAttributes);

    makeObservable(this);
  }

  /**
   * Clone a template.
   *
   * @param _cls: string (required)
   * @param campaign_id: string (required)
   * @param event_id: string (required)
   */
  public override async clone(data: ICloneProps): Promise<this> {
    return super.clone(data as Partial<Attributes<this>>);
  }

  public async preview(
    campaignId?: string,
    invitesCampaignId?: string,
    partnerId?: string,
    personId?: string,
  ): Promise<ITemplatePreviewPostResponse> {
    this.assertCollection(this.collection);

    // For account templates, don't try to extract eventId from URL
    let eventId: string | undefined;
    if (this.get('parent_kind') !== 'account') {
      const path = window.location.pathname.split('/');
      eventId = path[3];
    }

    const body: Partial<IPreviewBody> = {
      partner_id: partnerId,
      campaign_id: campaignId,
      invites_campaign_id: invitesCampaignId,
      person_id: personId,
      template: this.toJS(),
    };

    // Only include event_id if we have one (account templates don't need it)
    const effectiveEventId = this.get('event') || eventId;
    if (effectiveEventId) {
      body.event_id = effectiveEventId;
    }
    const response = await wretch<ITemplatePreviewPostResponse>(
      `${this.collection.url()}${this.id}/preview`,
      {
        method: 'POST',
        body: JSON.stringify(body),
        headers: this.collection.getHeaders(),
      },
    );
    if (isWretchError(response)) {
      throw response.error;
    }
    return response.data;
  }

  public async mergemap(
    campaignId?: string,
    invitesCampaignId?: string,
    partnerId?: string,
    personId?: string,
  ): Promise<ITemplateMergemap> {
    this.assertCollection(this.collection);
    // For account templates, don't try to extract eventId from URL
    let eventId: string | undefined;
    if (this.get('parent_kind') !== 'account') {
      const path = window.location.pathname.split('/');
      eventId = path[3];
    }

    const params = new URLSearchParams();
    const effectiveEventId = this.get('event') ?? eventId;
    if (effectiveEventId) {
      params.set('event_id', effectiveEventId);
    }
    if (!!partnerId) {
      params.set('partner_id', partnerId);
    }
    if (!!campaignId) {
      params.set('campaign_id', campaignId);
    }
    if (!!invitesCampaignId) {
      params.set('invites_campaign_id', invitesCampaignId);
    }
    if (!!personId) {
      params.set('person_id', personId);
    }
    const response = await wretch<ITemplateMergemap>(
      `${this.collection.url()}${this.id}/mergemap?${params.toString()}`,
      {
        method: 'GET',
        headers: this.collection.getHeaders(),
      },
    );
    if (isWretchError(response)) {
      throw response.error;
    }
    return response.data;
  }

  public async send(
    sendOptions: ISendOptions,
    campaignId?: string,
    invitesCampaignId?: string,
    partnerId?: string,
    personId?: string,
  ): Promise<IRachisMessage> {
    this.assertCollection(this.collection);

    // For account templates, don't try to extract eventId from URL
    let eventId: string | undefined;
    if (this.get('parent_kind') !== 'account') {
      const path = window.location.pathname.split('/');
      eventId = path[3];
    }

    const body: Partial<IPreviewBody> = {
      partner_id: partnerId,
      campaign_id: campaignId,
      invites_campaign_id: invitesCampaignId,
      person_id: personId,
      template: this.toJS(),
      ...sendOptions,
    };

    // Only include event_id if we have one (account templates don't need it)
    const effectiveEventId = this.get('event') || eventId;
    if (effectiveEventId) {
      body.event_id = effectiveEventId;
    }
    const response = await wretch<IRachisMessage>(`${this.collection.url()}${this.id}/send`, {
      method: 'POST',
      body: JSON.stringify(body),
      headers: this.collection.getHeaders(),
    });
    if (isWretchError(response)) {
      throw response.error;
    }
    return response.data;
  }

  public async editorURL(): Promise<string> {
    this.assertCollection(this.collection);
    const response = await wretch<ITemplateEditorURLResponse>(
      `${this.collection.url()}${this.id}/bannereditor`,
      {
        method: 'GET',
        headers: this.collection.getHeaders(),
      },
    );
    if (isWretchError(response)) {
      throw response.error;
    }
    return response.data.url;
  }

  public async sync(timestamp: number): Promise<void> {
    this.assertCollection(this.collection);
    this.isUpdating = true;
    const response = await wretch<ITemplate>(
      `${this.collection.url()}${this.id}/sync?ts=${timestamp}`,
      {
        method: 'GET',
        headers: this.collection.getHeaders(),
      },
    );
    if (isWretchError(response)) {
      throw response.error;
    }
    if (!this.get('banner_hash')) {
      this.set({ banner_hash: response.data.banner_hash });
    }
    this.set({
      banner_urls: response.data.banner_urls,
      height: response.data.height,
      name: response.data.name,
      width: response.data.width,
    });
    this.isUpdating = false;
  }

  public async createBanner(
    campaignId: string,
    partnerId: string,
    creativeId?: string,
  ): Promise<IRachisMessage> {
    this.assertCollection(this.collection);
    const body: Partial<IPreviewBody> = {
      partner_id: partnerId,
      campaign_id: campaignId,
      event_id: this.get('event'),
      template: this.toJS(),
    };
    if (creativeId) {
      body.creative_id = creativeId;
    }
    const response = await wretch<IRachisMessage>(
      `${this.collection.url()}${this.id}/create-banner`,
      {
        method: 'POST',
        body: JSON.stringify(body),
        headers: this.collection.getHeaders(),
      },
    );
    if (isWretchError(response)) {
      throw response.error;
    }
    return response.data;
  }

  public async getMentionableUsers(query?: string): Promise<IMentionableUser[]> {
    this.assertCollection(this.collection);
    const params = new URLSearchParams();
    if (query) {
      params.set('filter[name__icontains]', query);
    }
    const queryString = params.toString();
    const response = await wretch<IMentionableUser[]>(
      `${this.collection.url()}${this.id}/users${queryString ? `?${queryString}` : ''}`,
      {
        method: 'GET',
        headers: this.collection.getHeaders(),
      },
    );
    if (isWretchError(response)) {
      throw response.error;
    }
    return response.data;
  }

  public async notifyMentionedUsers(userIds: string[], commentId: string): Promise<IRachisMessage> {
    this.assertCollection(this.collection);
    const response = await wretch<IRachisMessage>(`${this.collection.url()}${this.id}/mention`, {
      method: 'POST',
      headers: this.collection.getHeaders(),
      body: JSON.stringify({
        user_ids: userIds,
        url: `${this.getItemUrl('edit/design')}?commentId=${commentId}`,
      }),
    });

    if (isWretchError(response)) {
      throw response.error;
    }

    return response.data;
  }

  public get isBanner(): boolean {
    return [TemplateClass.Banner, TemplateClass.ReferralBanner].includes(this.get('_cls'));
  }

  public shouldUsePlaceholder(): boolean {
    return !!this.get('bannersnack_enabled') && !this.get('banner_hash');
  }

  public getItemUrl(pathSuffix?: string): string {
    // Account templates use account settings routes
    if (this.get('parent_kind') === 'account') {
      return concatPath(`/settings/account/templates/${this.id}`, pathSuffix);
    }

    // Campaign templates use campaign routes
    if (this.get('parent_kind') === 'campaign') {
      return concatPath(
        `/projects/${this.get('event')}/campaigns/${this.get('parent')}/templates/${this.id}`,
        pathSuffix,
      );
    }

    // Default and project templates use project routes
    const path = window.location.pathname.split('/');
    const eventId = path[3];
    return concatPath(
      `/projects/${this.get('event') ?? eventId}/content/templates/${this.id}`,
      pathSuffix,
    );
  }

  public getTypeLabel(): string {
    const cls = this.get('_cls');
    return cls ? templateTypeLabel(cls) : 'Unknown';
  }

  public getDynamicContentMacro(dc: { _cls: DynamicContentClass; content_index: number }): string {
    switch (dc._cls) {
      case DynamicContentClass.CountdownTimer:
        return `@COUNTDOWN_TIMER_${dc.content_index}@`;

      case DynamicContentClass.Form:
        return `@FORM_${dc.content_index}@`;

      default:
        return `DYNAMIC_CONTENT_${dc.content_index}@`;
    }
  }

  @computed
  public get name(): string {
    return this.get('name', '').trim() || 'Unnamed Template';
  }

  public getMergefields(
    customFields: IObservableArray<CustomField>,
    isPinpoint?: boolean,
    isPartnerMessage?: boolean,
  ): IMergeField[] {
    let mergefields = this.getTemplateClassMergeFields();
    const isDefault = this.get('default');
    const isSES = this.get('_cls') === TemplateClass.SESEmail;
    if (isPartnerMessage) {
      const partnerCustomMergeFields = customFields
        // SES templates don't support list fields
        .filter(
          (f) =>
            [FieldCollection.Project, FieldCollection.Partner].includes(f.get('collection')) &&
            f.get('data_type') !== 'list',
        )
        .sort((a, b) =>
          a.get('u_key', '').localeCompare(b.get('u_key', ''), undefined, { sensitivity: 'base' }),
        )
        .map((f) => ({
          type: 'tag',
          name: `${f.get('collection')} ${f.get('u_key', '')}`,
          pinpointValue: `{{User.UserAttributes.cust_${
            f.get('u_key', '').toLowerCase().replaceAll(/\W/g, '_') || ''
          }}}`,
          value: `{{cust_${f.get('u_key', '').toLowerCase().replaceAll(/\W/g, '_')}}}`,
        }));
      mergefields = [
        ...sesRecipientMergeFields,
        ...sesPartnerMergeFields,
        ...partnerCustomMergeFields,
      ];
    } else if (!isDefault && !isSES) {
      const partnerParams = customFields
        .filter((p) => p.get('collection') === FieldCollection.Partner)
        .map((p) => ({
          type: 'tag',
          name: `(Partner) ${(p.get('u_key', '') || '').toUpperCase()}`,
          value: `@PARTNER.${(p.get('u_key', '') || '').toUpperCase()}@`,
          isCustom: true,
        }));
      mergefields = [...mergefields, ...partnerParams];
    } else if (!isDefault && isSES) {
      const recipientCustomFields = customFields
        // SES templates don't support list fields
        .filter(
          (f) =>
            [FieldCollection.Person].includes(f.get('collection')) && f.get('data_type') !== 'list',
        )
        // Case-insensitive alphabetical sort by user-friendly field name
        .sort((a, b) =>
          a.get('u_key', '').localeCompare(b.get('u_key', ''), undefined, { sensitivity: 'base' }),
        )
        // Convert fields to merge tag format
        .map((f) => ({
          type: 'tag',
          name: `${f.get('u_key', '')}`,
          pinpointValue: `{{User.UserAttributes.cust_${
            f.get('u_key', '').toLowerCase().replaceAll(/\W/g, '_') || ''
          }}}`,
          value: `{{cust_${f.get('u_key', '').toLowerCase().replaceAll(/\W/g, '_')}}}`,
        }));
      mergefields = [...mergefields, ...recipientCustomFields];
    }
    const dynamicContent = this.get('dynamic_content');
    if (dynamicContent) {
      const dynamicContentFields = dynamicContent.map((dc: IDynamicContent) => ({
        type: 'content',
        name: dc.name,
        value: this.getDynamicContentMacro(dc),
      }));
      mergefields = [...mergefields, ...dynamicContentFields];
    }

    if (isPinpoint) {
      mergefields = mergefields.map((f) => ({ ...f, value: f.pinpointValue || f.value }));
    }
    return mergefields;
  }

  private getTemplateClassMergeFields(): IMergeField[] {
    switch (this.get('_cls')) {
      case TemplateClass.Banner:
        return [
          ...defaultProjectMergeFields,
          ...defaultPartnerMergeFields,
          campaignDestinationUrlField,
          referralPageUrlField,
        ];

      case TemplateClass.ReferralEmail:

      case TemplateClass.Page:
        return [
          ...defaultProjectMergeFields,
          ...defaultPartnerMergeFields,
          participantPageUrlField,
        ];

      case TemplateClass.Email:

      case TemplateClass.LandingPage:
        return defaultProjectMergeFields;

      case TemplateClass.SESEmail:
        return [...sesRecipientMergeFields, ...sesPersonMergeFields];
    }
    return [...defaultProjectMergeFields, ...defaultPartnerMergeFields];
  }

  public getRowDisplayConditions(): never[] {
    return [];
  }

  @computed
  public get isSESTemplate(): boolean {
    return this.get('_cls') === TemplateClass.SESEmail;
  }

  /**
   * Returns value of attribute
   * @param attribute
   */
  public override get<K extends keyof ITemplate & string>(
    attribute: K,
  ): DeepObservable<ITemplate[K]>;
  /**
   * Returns value of attribute; defaultValue when value is not set
   * Note: defaultValue is not returned when value is falsey, but when value is null or undefined
   *
   * @param attribute
   * @param defaultValue
   */
  public override get<K extends keyof ITemplate & string>(
    attribute: K,
    defaultValue: Exclude<ITemplate[K], undefined>,
  ): DeepObservable<Exclude<ITemplate[K], undefined>>;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public override get<K extends keyof ITemplate & string>(attribute: K, defaultValue?: any): any {
    const value = super.get(attribute, defaultValue);
    // TODO: Remove hack to handle old templates with json that are objects instead of strings
    if (attribute === 'content' && typeof value.json !== 'string') {
      return { ...value, json: JSON.stringify(value.json) };
    }
    return value;
  }

  @action
  public addMergefield(newValue: string): void {
    this.set({
      used_mergefields: [...this.get('used_mergefields'), newValue],
    });
  }
}

// TODO: This is probably not needed once we are querying templates with ViewMixin
export interface ITemplateListParams extends IListParams<ITemplate> {
  defaults: 0 | 1;
}

export class Templates extends Collection<Template> {
  public override async clone(
    id: string,
    data: Pick<ITemplate, 'campaign_id' | 'event_id' | '_cls'>,
  ): Promise<Template> {
    return super.clone(id, data);
  }

  public getModel(attributes = {}): Template {
    return new Template(attributes);
  }

  public getClassName(): string {
    return 'templates';
  }

  public override url(variant?: string, value?: string | string[]): string {
    // All templates use the same endpoint since template IDs are unique
    // whether accessed via projects or accounts
    return super.url(variant, value);
  }

  /**
   * @param listId Use the useId() hook to generate a unique listID.
   */
  public override list(
    params: Partial<ITemplateListParams>,
    options: Partial<IListOptions> = {},
    listId?: string,
  ): ListResponse<Template> {
    return super.list(params, options, listId);
  }
}
